import type { AlertEnvelope } from '@fleetcore/shared/telemetry/alerts'
import { X } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import { SeverityBadge } from './badges'
import { MAX_VISIBLE_TOASTS } from './use-alert-stream'
import { alertCentrePath } from './routes'
import './i18n'

/**
 * The arrival toasts.
 *
 * ─── `role="status"`, NOT `role="alert"` — AND THAT IS DELIBERATE ───────────
 *
 * `alert` interrupts a screen reader mid-sentence. On a feed that can legitimately
 * receive six arrivals in a minute that turns the product unusable for the person
 * relying on it, which is a strange way to make an alarm system more accessible. The
 * region is polite and the container is `aria-live="polite"`; the CRITICAL toast is
 * the one exception and is marked assertive, because that one is worth interrupting
 * for.
 *
 * ─── AT MOST THREE, THEN A COUNT ────────────────────────────────────────────
 *
 * Rule 1 applies to the UI too. Forty stacked toasts are the same "cried wolf"
 * failure rendered client-side: the screen becomes unusable and the user closes the
 * tab, which is worse than showing them a number. The engine has already
 * deduplicated and rate-limited; this is the last line of the same defence.
 */

export interface AlertToastsProps {
  readonly alerts: readonly AlertEnvelope[]
  readonly onDismiss: (alertId: string) => void
  /** Called when the user follows a toast into the feed. */
  readonly onOpen?: (alertId: string) => void
}

export function AlertToasts({ alerts, onDismiss, onOpen }: AlertToastsProps) {
  const { t } = useTranslation()
  const formatters = useFormatters()

  if (alerts.length === 0) return null

  const visible = alerts.slice(0, MAX_VISIBLE_TOASTS)
  const hidden = alerts.length - visible.length

  return (
    <div
      aria-live="polite"
      aria-label={t('alerts:toast.newAlert')}
      className="pointer-events-none fixed bottom-4 end-4 z-50 flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
    >
      {visible.map((envelope) => (
        <div
          key={envelope.alertId}
          data-testid="alert-toast"
          data-severity={envelope.severity}
          role="status"
          aria-live={envelope.severity === 'critical' ? 'assertive' : 'polite'}
          className={cn(
            'pointer-events-auto rounded-lg border border-l-4 bg-surface p-3 shadow-card',
            envelope.severity === 'critical'
              ? 'border-danger border-s-danger'
              : envelope.severity === 'warning'
                ? 'border-border border-s-warning'
                : 'border-border border-s-info',
          )}
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <SeverityBadge severity={envelope.severity} />
                <span className="text-xs tabular-nums text-foreground-muted">
                  {formatters.time(envelope.occurredAt)}
                </span>
              </div>
              <p className="mt-1 truncate text-sm font-semibold text-foreground">
                {t(`alerts:eventType.${envelope.eventType}`)}
              </p>
              <Link
                to={alertCentrePath({ alertId: envelope.alertId })}
                onClick={() => onOpen?.(envelope.alertId)}
                className="mt-1 inline-block text-xs font-medium text-accent-text hover:underline"
              >
                {t('alerts:toast.viewAll')}
              </Link>
            </div>

            <button
              type="button"
              aria-label={t('alerts:toast.dismiss')}
              onClick={() => onDismiss(envelope.alertId)}
              className="rounded p-1 text-foreground-subtle hover:bg-surface-hover"
            >
              <X aria-hidden className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}

      {hidden > 0 ? (
        <p className="pointer-events-auto rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-foreground-muted shadow-card">
          {t('alerts:feed.unread', { count: hidden })}
        </p>
      ) : null}
    </div>
  )
}
